<template>
  <ContentSection
    id="lighting-advisor-results"
    color-mode="dark"
    class="content-section--fullwidth lighting-advisor-results"
    data-testid="lighting-advisor-results-section"
  >
    <div
      v-if="answerSummary.length"
      class="lighting-advisor-results__summary"
      data-testid="lighting-advisor-show-answers"
    >
      <AccordionComponent
        :item-heading-level="4"
        @click="handleAnswersAccordionClick"
      >
        <AccordionItem uid="lighting-advisor-results-summary" :is-open="false">
          <template #title>
            {{
              $textDictionary['lightingAdvisor.results.showAnswers'] ??
              'Show answers'
            }}
          </template>
          <div class="lighting-advisor-results__summary-values">
            <p
              v-for="item in answerSummary"
              :key="`${item.stepId}-${item.label}`"
            >
              <span class="feature-name">{{ item.label }}</span>
              <span class="feature-value">{{ item.value }}</span>
            </p>
          </div>
        </AccordionItem>
      </AccordionComponent>
    </div>

    <div
      v-if="globalWarnings.length"
      class="lighting-advisor-results__global-warnings"
    >
      <AlertBox
        v-for="(warning, index) in globalWarnings"
        :key="warning.identifier"
        icon="attention"
        :style="'warning'"
        :data-testid="`lighting-advisor-results-global-warning-${index + 1}`"
      >
        <div
          class="rte-text"
          v-html="resolveWarningMessage(warning.identifier)"
        />
      </AlertBox>
    </div>

    <div class="lighting-advisor-results__header">
      <p
        class="lighting-advisor-results__count"
        data-testid="lighting-advisor-results-count"
      >
        {{
          (
            $textDictionary['lightingAdvisor.results.count'] ??
            'Your results ({total})'
          ).replace('{total}', String(total))
        }}
      </p>
      <SegmentControls
        v-if="showViewToggle"
        :controls="viewControls"
        class="is-secondary lighting-advisor-results__view-toggle"
        data-testid="lighting-advisor-results-view-toggle"
        @change="handleViewChange"
      />
    </div>

    <ProductList
      v-if="showLoadingSkeleton"
      :items="loadingProductList.items"
      :display-mode="displayMode"
      :default-columns="loadingProductList.defaultColumns"
      :attribute-columns="loadingProductList.attributeColumns"
      size="full"
      class="lighting-advisor-results__list"
      data-testid="lighting-advisor-results-list"
    />

    <ProductList
      v-else-if="activeProductList.items.length"
      :key="displayMode"
      :items="activeProductList.items"
      :display-mode="displayMode"
      :default-columns="activeProductList.defaultColumns"
      :attribute-columns="activeProductList.attributeColumns"
      size="full"
      class="lighting-advisor-results__list"
      data-testid="lighting-advisor-results-list"
      @click="handleProductListClick"
    >
      <template #card-footer="productCardProps">
        <p
          v-if="debugScoreForSku(productCardProps.product.sku) !== null"
          class="lighting-advisor-results__debug-score"
          :data-testid="`lighting-advisor-debug-score-${productCardProps.product.sku}`"
        >
          Debug score: {{ debugScoreForSku(productCardProps.product.sku) }}
        </p>

        <AlertBox
          v-if="warningsForSku(productCardProps.product.sku).length"
          icon="attention"
          :style="'neutral'"
          class="lighting-advisor-results__warning-alert"
          :data-testid="`lighting-advisor-result-warning-${productCardProps.product.sku}`"
          role="button"
          tabindex="0"
          aria-haspopup="dialog"
          @click="
            openWarningsForSku(
              productCardProps.product.sku ?? '',
              productCardProps.product.name ?? null
            )
          "
          @keydown.enter.prevent="
            openWarningsForSku(
              productCardProps.product.sku ?? '',
              productCardProps.product.name ?? null
            )
          "
          @keydown.space.prevent="
            openWarningsForSku(
              productCardProps.product.sku ?? '',
              productCardProps.product.name ?? null
            )
          "
        >
          <span>
            {{ warningsForSku(productCardProps.product.sku).length }}
            {{
              warningsForSku(productCardProps.product.sku).length === 1
                ? ($textDictionary[
                    'lightingAdvisor.results.warningCount.one'
                  ] ?? 'important note for this product. click here')
                : ($textDictionary[
                    'lightingAdvisor.results.warningCount.other'
                  ] ?? 'important notes for this product. click here')
            }}
          </span>
        </AlertBox>

        <Button
          v-if="productCardProps.product.sku"
          type="button"
          style-type="attraction"
          :data-testid="`lighting-advisor-select-${productCardProps.product.sku}`"
          @click="
            emit('select', {
              sku: productCardProps.product.sku,
              name:
                productCardProps.product.name ?? productCardProps.product.sku
            })
          "
        >
          {{ $textDictionary['lightingAdvisor.common.select'] ?? 'Select' }}
        </Button>
      </template>
    </ProductList>

    <div v-else class="lighting-advisor-results__empty">
      <p data-testid="lighting-advisor-zero-results">
        {{
          $textDictionary['lightingAdvisor.results.empty'] ??
          'No lights match your selection.'
        }}
      </p>
      <Button
        type="button"
        style-type="attraction"
        data-testid="lighting-advisor-support-cta"
        @click="openSupport"
      >
        {{
          $textDictionary['lightingAdvisor.results.supportCta'] ??
          'Contact support'
        }}
      </Button>
    </div>

    <GlobalSearchPagination
      :current-page="currentPage"
      :total-pages="totalPages"
      :prev-label="
        $textDictionary['productsList.pagination.previous'] ?? 'Previous page'
      "
      :next-label="
        $textDictionary['productsList.pagination.next'] ?? 'Next page'
      "
      class="lighting-advisor-results__pagination"
      data-testid="lighting-advisor-results-pagination"
      @page-change="(page) => emit('page-change', page)"
    />

    <LightingAdvisorWarningsModal
      v-model="warningsModalOpen"
      :product-name="warningsModalProductName"
      :warnings="warningsModalItems"
    />
  </ContentSection>
</template>

<script setup lang="ts">
import {
  AccordionComponent,
  AccordionItem,
  AlertBox,
  Button,
  ContentSection,
  ProductList,
  SegmentControls
} from '@hypercodestudio/basler-components';
import type { controlBtn } from '@hypercodestudio/basler-components';

import type { ProductsBySkusFragment } from '~~/lib/Shop/generated/schema';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { prefetchMagentoProductListAttributeMetadata } from '~/composables/useMagentoProductListAttributeMetadata';
import { createProductListLoadingItems } from '~/utils/shop/createProductListLoadingItems';
import { prepareProductListDisplay } from '~/utils/shop/prepareProductListDisplay';
import { buildLightingAdvisorResultCardPropsList } from '~/utils/lightingAdvisor/buildLightingAdvisorResultCards';
import type { LightingAdvisorDecoration } from '~/utils/lightingAdvisor/catalogTypes';
import type { LightingSelectionSummaryItem } from '~/utils/lightingAdvisor/buildLightingSelectionSummary';
import {
  partitionLightingAdvisorDecorations,
  resolveLightingAdvisorWarningMessage
} from '~/utils/lightingAdvisor/warningMessages';
import type { ProductListView } from '~/stores/productFilterStore/helper/resolveProductListView';
import LightingAdvisorWarningsModal from '~/components/global/lib/LightingAdvisor/LightingAdvisorWarningsModal.vue';
import GlobalSearchPagination from '~/components/global/lib/Search/GlobalSearchPagination.vue';

interface Props {
  lightSkus: string[];
  /** Raw scoring result per sku, rendered for debugging outside prod live. */
  scoreBySku?: Record<string, number>;
  decorations: LightingAdvisorDecoration[];
  answerSummary?: LightingSelectionSummaryItem[];
  loading?: boolean;
  total: number;
  currentPage: number;
  totalPages: number;
  pageSize?: number;
}

type MagentoProduct = NonNullable<
  NonNullable<ProductsBySkusFragment['items']>[number]
>;

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  pageSize: 24,
  answerSummary: () => [],
  scoreBySku: () => ({})
});

const emit = defineEmits<{
  (event: 'page-change', page: number): void;
  (event: 'select', product: { sku: string; name: string }): void;
}>();

const { $activeLocale, $shopStore, $textDictionary, $currentEnvName } =
  useNuxtApp();
const createProductDetailLink = useCreateProductDetailLink();
const logger = useLogger();
const analytics = useLightingAdvisorAnalytics();

const cardsLoading = ref(false);
const displayMode = ref<ProductListView>('grid');
const magentoProducts = ref<MagentoProduct[]>([]);
const productListItems = ref<
  ReturnType<typeof buildLightingAdvisorResultCardPropsList>
>([]);
const warningsModalOpen = ref(false);
const warningsModalProductName = ref<string | null>(null);
const warningsModalItems = ref<LightingAdvisorDecoration[]>([]);
const answersOpen = ref(false);
let cardsAbortController: AbortController | null = null;

const partitionedDecorations = computed(() =>
  partitionLightingAdvisorDecorations(props.decorations)
);

const globalWarnings = computed(
  () => partitionedDecorations.value.globalWarnings
);

function resolveWarningMessage(identifier: string): string {
  return resolveLightingAdvisorWarningMessage(
    identifier,
    ($textDictionary.value ?? {}) as Record<string, string | undefined>
  );
}

// Mirrors the FRC version output: everything except prod live, see plugins/07.env
const showDebugScore = computed(() => $currentEnvName.value !== 'master');

function debugScoreForSku(sku: string | undefined): number | null {
  if (!sku || !showDebugScore.value) {
    return null;
  }
  return props.scoreBySku[sku] ?? null;
}

function warningsForSku(sku: string | undefined): LightingAdvisorDecoration[] {
  if (!sku) {
    return [];
  }
  return partitionedDecorations.value.warningsBySku[sku] ?? [];
}

function openWarningsForSku(sku: string, productName: string | null): void {
  warningsModalItems.value = warningsForSku(sku);
  warningsModalProductName.value = productName;
  warningsModalOpen.value = true;
  analytics.trackWarningDetails();
}

function handleAnswersAccordionClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('button')) {
    return;
  }
  analytics.trackEvent(
    'question_btn',
    answersOpen.value ? 'Hide Questions' : 'Show Questions'
  );
  answersOpen.value = !answersOpen.value;
}

const showLoadingSkeleton = computed(() => props.loading || cardsLoading.value);

const showViewToggle = computed(
  () => showLoadingSkeleton.value || productListItems.value.length > 0
);

const productListColumnLabels = computed(() => ({
  media: $textDictionary.value['productsList.tableList.column.media'] ?? '',
  title: $textDictionary.value['productsList.tableList.column.title'] ?? '',
  priceText:
    $textDictionary.value['productsList.tableList.column.priceText'] ?? '',
  description:
    $textDictionary.value['productsList.tableList.column.description'] ?? ''
}));

const viewControls = computed(() => [
  {
    id: 'grid',
    title:
      $textDictionary.value['productsLists.viewControls.grid.title'] ?? 'Grid',
    isActive: displayMode.value === 'grid',
    iconName: 'grid-2x2'
  },
  {
    id: 'table-list',
    title:
      $textDictionary.value['productsLists.viewControls.tableList.title'] ??
      'List',
    isActive: displayMode.value === 'table-list',
    iconName: 'list-square'
  }
]);

const loadingProductList = computed(() =>
  prepareProductListDisplay(
    createProductListLoadingItems(Math.min(props.pageSize, 8)),
    {
      displayMode: displayMode.value,
      includePriceColumn: true,
      columnLabels: productListColumnLabels.value
    }
  )
);

const activeProductList = computed(() =>
  prepareProductListDisplay(productListItems.value, {
    displayMode: displayMode.value,
    includePriceColumn: true,
    columnLabels: productListColumnLabels.value
  })
);

function handleViewChange(view: controlBtn): void {
  if (view.id !== 'grid' && view.id !== 'table-list') {
    return;
  }
  displayMode.value = view.id;
}

function handleProductListClick(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('a')) {
    return;
  }
  const card = target.closest<HTMLElement>(
    '[data-testid^="lighting-advisor-result-card-"]'
  );
  const testId = card?.dataset.testid;
  const prefix = 'lighting-advisor-result-card-';
  if (!testId?.startsWith(prefix)) {
    return;
  }
  const sku = testId.slice(prefix.length);
  const product = productListItems.value.find(
    (item) => item.product.sku === sku
  )?.product;
  analytics.trackEvent('productdetails', `${product?.name ?? sku} (${sku})`);
}

function rebuildCardProps(): void {
  productListItems.value = buildLightingAdvisorResultCardPropsList(
    props.lightSkus,
    magentoProducts.value,
    {
      textDictionary: $textDictionary.value ?? {},
      locale: $activeLocale.value.webapp,
      createProductDetailLink,
      decorations: props.decorations
    }
  );
}

async function loadProductCards(skus: string[]): Promise<void> {
  cardsAbortController?.abort();
  if (!skus.length) {
    cardsAbortController = null;
    cardsLoading.value = false;
    magentoProducts.value = [];
    productListItems.value = [];
    return;
  }

  const controller = new AbortController();
  cardsAbortController = controller;
  cardsLoading.value = true;
  try {
    const response = await $fetch<ProductsBySkusFragment>(
      '/api/magento/productsBySkus',
      {
        signal: controller.signal,
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          pageSize: skus.length,
          skus
        }
      }
    );

    const items: MagentoProduct[] = (response.items ?? []).filter(
      (
        item: NonNullable<ProductsBySkusFragment['items']>[number]
      ): item is MagentoProduct => Boolean(item?.sku)
    );
    if (controller.signal.aborted) {
      return;
    }

    await prefetchMagentoProductListAttributeMetadata(
      $shopStore.value,
      $activeLocale.value.webapp,
      items.map((item: MagentoProduct) => item.sku)
    );
    if (controller.signal.aborted) {
      return;
    }

    magentoProducts.value = items;
    rebuildCardProps();
  } catch (error) {
    if (controller.signal.aborted) {
      return;
    }
    logger.error('Lighting advisor result cards failed', error);
    magentoProducts.value = [];
    productListItems.value = [];
  } finally {
    if (cardsAbortController === controller) {
      cardsAbortController = null;
      cardsLoading.value = false;
    }
  }
}

function openSupport(): void {
  analytics.trackSupport('Results List');
  void navigateTo(
    $textDictionary.value['lightingAdvisor.results.supportUrl'] ??
      '/en/support/contact/'
  );
}

watch(
  () => props.lightSkus.join(','),
  () => {
    void loadProductCards(props.lightSkus);
  },
  { immediate: true }
);

watch(
  () => props.decorations,
  () => {
    if (magentoProducts.value.length) {
      rebuildCardProps();
    }
  },
  { deep: true }
);

onBeforeUnmount(() => {
  cardsAbortController?.abort();
});
</script>

<style scoped>
.lighting-advisor-results {
  margin-top: 2rem;
}

.lighting-advisor-results__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.lighting-advisor-results__count {
  margin: 0;
}

.lighting-advisor-results__empty {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lighting-advisor-results__pagination {
  margin-top: 2rem;
}

.lighting-advisor-results__warning-alert {
  cursor: pointer;
}

.lighting-advisor-results__debug-score {
  margin: 0 0 0.5rem;
  font-family: monospace;
  font-size: 0.75rem;
  opacity: 0.7;
}

.lighting-advisor-results__summary {
  margin-bottom: 1.5rem;
}

.lighting-advisor-results__summary-values p {
  margin: 0 0 0.25rem;
}

.feature-name {
  margin-right: 0.5rem;
  opacity: 0.85;
}

.feature-value {
  font-weight: 600;
}
</style>
